/**
 * Collapsible section disclosure for the Profile editor drawer (ADR-033 §5;
 * REQ-UI-076 / REQ-UI-094).
 *
 * Reuses the **Entity Details** section disclosure structure — the chevron +
 * title disclosure button from :func:`SectionShell`
 * (``ui/src/entity-details/section-shell.tsx``, REQ-UI-064 / REQ-UI-092) — so
 * the editor's section groups read **identically** to the live Entity Details
 * panel's sections (2026-06-09 — align the Settings panel to the Entity Details
 * look-and-feel): the same flush, divider-separated section (``border-b
 * border-border last:border-b-0`` — NOT a rounded bordered card, and with no
 * gap between sections), the same ``sticky`` ``bg-bg-surface`` header, and the
 * same all-caps ``text-caption ... uppercase tracking-wider`` title + chevron
 * disclosure. The two now differ ONLY in their persistence model:
 *
 *   - ``SectionShell`` drives its open/collapse off the per-panel
 *     :func:`EntityDetailsSectionsContext` (persisted to user preferences).
 *   - This component is **local, ephemeral state** — the editor's sections are
 *     a transient authoring surface, not a saved panel layout, so a section's
 *     open/collapse is not persisted. **Every editor section starts EXPANDED**
 *     (2026-06-12 — every setting is visible at a glance; the operator collapses
 *     what they don't need. Was collapsed-by-default through item 7e before; this
 *     is the one place the Settings panel deliberately diverges from the Entity
 *     Details disclosure, which defaults per its persisted per-section state).
 *
 * The whole header treatment (the flush divider + sticky ``bg-bg-surface``
 * header, the ``CaretDown`` / ``CaretRight`` 12 px bold chevron, the all-caps
 * title, the ``-mx-1`` rounded hover hit-target, the ``focus-visible:ring-1
 * focus-visible:ring-brand`` ring) is copied from ``SectionShell`` so the two
 * stay in lock-step; if that treatment changes there, mirror it here.
 *
 * Accessibility (REQ-UI-094 / REQ-UI-059): a real ``<button type="button">``
 * with ``aria-expanded`` reflecting the open state + ``aria-controls`` pointing
 * at the body; keyboard-operable (native Enter/Space); the chevron is
 * ``aria-hidden``.
 *
 * Cross-references:
 *   - ADR-033 §5 (editor drawer); REQ-UI-076 (editor); REQ-UI-094 (a11y).
 *   - REQ-UI-064 / REQ-UI-092 (the Entity Details collapsible pattern reused).
 *   - ui/src/entity-details/section-shell.tsx (the source of the treatment).
 */
import { useState, type ReactNode } from "react";
import { CaretDown, CaretRight } from "@phosphor-icons/react";

export interface ProfileEditorSectionProps {
  /** Display title — rendered as the all-caps ``text-caption … uppercase
   *  tracking-wider`` heading matching the Entity Details ``SectionShell``. */
  title: string;
  /** Per-section test-id root — the section is ``profile-editor-section-<testId>``
   *  and its toggle is ``profile-editor-section-<testId>-toggle``. */
  testId: string;
  /** Section body content (rendered only while expanded). */
  children: ReactNode;
}

/**
 * A titled, collapsible editor section. Starts **expanded** (2026-06-12);
 * clicking the header toggles the body. Mirrors the Entity Details
 * :func:`SectionShell` disclosure treatment for visual consistency.
 */
export function ProfileEditorSection({
  title,
  testId,
  children,
}: ProfileEditorSectionProps): React.ReactElement {
  // Local, non-persisted state — every editor section starts EXPANDED (2026-06-12;
  // was collapsed-by-default before — the operator now sees all settings at once).
  const [expanded, setExpanded] = useState(true);
  const bodyId = `profile-editor-section-${testId}-body`;
  return (
    <section
      data-testid={`profile-editor-section-${testId}`}
      data-expanded={expanded}
      className="border-b border-border last:border-b-0"
    >
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-bg-surface px-4 py-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={bodyId}
          data-testid={`profile-editor-section-${testId}-toggle`}
          className="-mx-1 flex min-w-0 flex-1 items-center gap-1.5 rounded px-1 text-left hover:bg-bg-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-brand"
        >
          {expanded ? (
            <CaretDown size={12} weight="bold" aria-hidden className="shrink-0 text-text-muted" />
          ) : (
            <CaretRight size={12} weight="bold" aria-hidden className="shrink-0 text-text-muted" />
          )}
          <h3 className="truncate text-caption font-medium uppercase tracking-wider text-text-secondary">
            {title}
          </h3>
        </button>
      </header>
      {expanded && (
        <div id={bodyId} className="flex flex-col gap-2 px-4 py-3">
          {children}
        </div>
      )}
    </section>
  );
}
